import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { PERMISSIONS, type AtRiskAccount } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { ProblemCallout } from '@/components/ops/Problem';
import { BenchmarksPanel, FlagCard } from '@/components/scoring';
import { formatMoney, humanizeStatus } from '@/components/subscription';
import { formatPercent } from '@/components/format';

/**
 * The work queue: accounts with raised flags, worst revenue first.
 *
 * <b>Revenue-ordered is the whole point of this page.</b> The same three flags matter more on an
 * account paying £900 a month than on a free trial, and a list sorted by flag count sends somebody
 * to the noisiest tenant rather than the most expensive one to lose. That ordering is what turns
 * this service from a dashboard into something a person works through on a Monday morning.
 */
export function AtRiskPage() {
  const [includeInternal, setIncludeInternal] = useState(false);

  const queue = useQuery({
    queryKey: ['at-risk', includeInternal],
    queryFn: () => api.scores.atRisk(includeInternal),
  });

  const benchmarks = useQuery({
    queryKey: ['benchmarks'],
    queryFn: () => api.scores.benchmarks(),
  });

  const cohorts = useQuery({
    queryKey: ['cohorts', includeInternal],
    queryFn: () => api.scores.cohorts(includeInternal),
  });

  const accounts = queue.data ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="t-display" style={{ margin: 0 }}>At risk</h1>
          <p className="t-body muted" style={{ margin: '4px 0 0' }}>
            Accounts with at least one raised flag, most revenue first.
          </p>
        </div>
        <button
          type="button"
          className={`btn ${includeInternal ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setIncludeInternal((v) => !v)}
        >
          {includeInternal ? 'Including internal' : 'Customers only'}
        </button>
      </div>

      <div className="card">
        {queue.isPending ? (
          <div className="empty-state t-body">Loading…</div>
        ) : queue.isError ? (
          // Checked before "empty": a failed read is NOT "no account has a raised flag".
          <div className="card-body"><ProblemCallout error={queue.error} title="Could not load the at-risk queue" /></div>
        ) : accounts.length === 0 ? (
          <div className="empty-state t-body">
            No account has a raised flag.
            <div className="t-label subtle" style={{ marginTop: 4 }}>
              Flags are recomputed after each collector run. An anomaly must be seen on two
              consecutive runs before it is listed here.
            </div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table t-body">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Account</th>
                  {hasPermission(PERMISSIONS.subscriptionRead) && <th className="num">MRR</th>}
                  <th className="num">Health</th>
                  <th className="num">Flags</th>
                  <th style={{ textAlign: 'left' }}>Why</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => <QueueRow key={a.tenant_id} account={a} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {benchmarks.data && <BenchmarksPanel benchmarks={benchmarks.data} />}

      {cohorts.data && cohorts.data.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>Retention by signup month</h2>
            <span className="t-label subtle">active in the last 30 days</span>
          </div>

          <div className="table-wrap">
            <table className="table t-body">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Cohort</th>
                  <th className="num">Accounts</th>
                  <th className="num">Still active</th>
                  <th className="num">Retention</th>
                </tr>
              </thead>
              <tbody>
                {cohorts.data.map((c) => (
                  <tr key={c.cohort_month}>
                    <td className="mono">{c.cohort_month}</td>
                    <td className="num mono">{c.tenants}</td>
                    <td className="num mono">{c.still_active}</td>
                    {/* Null on an empty cohort — there is nothing to retain, which is not 0%. */}
                    <td className="num mono">{formatPercent(c.retention_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function QueueRow({ account }: { account: AtRiskAccount }) {
  return (
    <tr>
      <td>
        <Link to={`/accounts/${account.tenant_id}`}>{account.name ?? account.tenant_id}</Link>
        <div className="t-caption subtle">{humanizeStatus(account.subscription_status)}</div>
      </td>

      {/* An unreplicated price is an em dash, never £0. Unknown revenue is not no revenue — and it
          sorts last on the server for the same reason. */}
      {hasPermission(PERMISSIONS.subscriptionRead) && <td className="num mono">{formatMoney(account.mrr_minor, account.currency)}</td>}

      <td className="num mono">
        {account.health_score === null ? '—' : Math.round(account.health_score)}
      </td>

      <td className="num mono">{account.risk_level}</td>

      <td style={{ maxWidth: 480 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          {account.flags.map((f) => <FlagCard key={f.id} flag={f} />)}
        </div>
      </td>
    </tr>
  );
}
